import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import algoliasearch from 'algoliasearch/lite'
import { InstantSearch, Configure } from 'react-instantsearch-dom'
import qs from 'qs'
import { trackAlgoliaEvent } from './analytics'

const searchClient = algoliasearch(
  window.__GLOBALS__.config.algoliaApp,
  window.__GLOBALS__.config.algoliaSearch,
)

/**
 * Builds the object used for refinementList queries
 *
 * @param {object} refinementList - refinementList object from algolia searchState
 * @param {function} stringFormatter - Either encodeURIComponent or decodeURIComponent
 *
 * @returns {object} - object used for building a url/searchState
 */
const buildRefinementList = (refinementList, stringFormatter) =>
  Object.entries(refinementList).reduce((accumulator, currentValue) => {
    const [key, value] = currentValue

    if (value && Array.isArray(value)) {
      accumulator[stringFormatter(key)] = value.map((refinementValue) =>
        stringFormatter(refinementValue),
      )
    }

    return accumulator
  }, {})

/**
 * Builds the object used for range queries
 *
 * @param {object} range - Range object from algolia searchState
 * @param {function} stringFormatter - Either encodeURIComponent or decodeURIComponent
 *
 * @returns {object} - object used for building a url/searchState
 */
const buildRange = (range, stringFormatter) =>
  Object.entries(range).reduce((accumulator, currentValue) => {
    const [key, value] = currentValue
    const rangeParams = {}

    if (value.min !== undefined) {
      rangeParams.min = Number(value.min)
    }

    if (value.max !== undefined) {
      rangeParams.max = Number(value.max)
    }

    accumulator[stringFormatter(key)] = rangeParams

    return accumulator
  }, {})

/**
 * Builds the object used for toggle queries
 *
 * @param {object} toggle - Toggle object from algolia searchState
 *
 * @returns {object} - object used for building a url/searchState
 */
const buildToggle = (toggle) =>
  Object.entries(toggle).reduce((accumulator, currentValue) => {
    const [key, value] = currentValue
    const toggleValue = Number(value)

    if (toggleValue) {
      accumulator[key] = 1
    }

    return accumulator
  }, {})

/**
 * Creates a URL from the search state
 *
 * @param {object} searchState - Algolia searchState object
 *
 * @returns {string} Query string
 */
const searchStateToUrl = (searchState = {}) => {
  const { query = '', page = 1, refinementList = {}, range = {}, toggle = {} } = searchState

  const queryParameters = {}

  if (query) {
    queryParameters.query = encodeURIComponent(query)
  }

  if (page !== 1) {
    queryParameters.page = page
  }

  if (Object.values(refinementList).filter(Boolean).length) {
    // Encodes params from refinementList, each value should be a string
    queryParameters.filters = buildRefinementList(refinementList, encodeURIComponent)
  }

  if (Object.values(range).filter(Boolean).length) {
    // Encodes params from range, each value should contain a min/max value
    queryParameters.range = buildRange(range, encodeURIComponent)
  }

  if (Object.values(toggle).filter(Boolean).length) {
    // Encodes params from range, each value should contain a min/max value
    queryParameters.toggle = buildToggle(toggle)
  }

  const queryString = qs.stringify(queryParameters, {
    addQueryPrefix: true,
  })

  return `${location.pathname}${queryString}`
}

/**
 * Converts url to a search object
 *
 * @param {object} location - Url location object
 *
 * @returns {string} Algolia search object
 */
const urlToSearchState = (location) => {
  const { query = '', page = 1, filters = {}, range = {}, toggle = {} } = qs.parse(
    location.search.slice(1),
  )

  const decodedRefinementList = buildRefinementList(filters, decodeURIComponent)

  const decodedRange = buildRange(range, decodeURIComponent)

  const decodedToggle = buildToggle(toggle)

  return {
    query: decodeURIComponent(query),
    page,
    refinementList: {
      ...decodedRefinementList,
    },
    range: {
      ...decodedRange,
    },
    toggle: {
      ...decodedToggle,
    },
  }
}

/**
 * Generates facetFilters from the pathname.
 * Ex. "/t/books" -> "taxon.taxonomies:Books"
 *
 * @param {object} facets - Contains current taxon/taxonomy info
 *
 * @returns {array} - array of facetFilters
 */
const getFacetFiltersFromProps = (facets = {}) => {
  const { taxon = '', taxonomy = '', permalink = '' } = facets

  if (!taxon && !taxonomy && !permalink) {
    return []
  }

  const facetFilters = []
  const pathnameLength = (location.pathname || '').split('/').filter(Boolean).length

  // taxon pages like "/t/catalog/2021/mothers-day" require a permalink to specify results for that year only.
  if (pathnameLength >= 4 && permalink) {
    facetFilters.push(`taxons.permalink:${permalink}`)
  }

  if (taxonomy) {
    facetFilters.push(`taxons.taxonomy:${taxonomy}`)
  }

  // The taxon will be the same as the taxonomy if there is no specific taxonomy
  // example: for pathname /t/books taxon = "Books" and taxonomy = "Books"
  if (taxon && taxon !== taxonomy) {
    facetFilters.push(`taxons.name:${taxon}`)
  }

  trackAlgoliaEvent('viewedFilters', {
    eventName: 'Filters Viewed',
    filters: facetFilters,
  })

  return facetFilters
}

/**
 * Compares state objects for equality, uses recursion for nested objects
 * We also skip the current configuration as that does not build the search state
 *
 * @param {object} obj1 - One instance of a state object
 * @param {object} obj2 - Another instance of a state object
 *
 * @returns {boolean} Whether or not the search states are equal
 */
const isSearchStateEqual = (obj1, obj2) => {
  let largerObj = obj1
  let smallerObj = obj2

  if (Object.keys(obj1).length < Object.keys(obj2).length) {
    largerObj = obj2
    smallerObj = obj1
  }

  return Object.entries(largerObj).every(([key, value1]) => {
    if (key === 'configure') {
      return true
    }

    const value2 = smallerObj[key]

    // If an entry is an object we call the function again, otherwise compare the two values
    if (!!value1 && typeof value1 === 'object' && !!value2 && typeof value2 === 'object') {
      return isSearchStateEqual(value1, value2)
    }

    return value1 === value2
  })
}

/**
 * Wrapper component for the algolia widgets
 *
 * This component is responsible for connecting us to Algolia, and configuring search results. It updates/controls the searchState by updating the query within the URL. It also derives the current searchState from the URL on pageLoad.
 *
 * This component is where we explicitly state which attributes we want from the product object.
 *
 * It must wrap any components that use Algolia-related features.
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/configure/react/
 *
 * @param {boolean} isRoot - Whether we should use the pathname to apply additional filters
 * @param {any} children - React component(s)
 *
 * @returns {function} React component
 */
export const AlgoliaContainer = ({ isRoot = false, facets, children }) => {
  const [searchState, setSearchState] = useState(urlToSearchState(location))
  const [debouncedSetState, setDebouncedSetState] = useState(null)
  const facetFilters = isRoot ? [] : getFacetFiltersFromProps(facets)

  /**
   * Updates the search state and URL
   *
   * @param {object} updatedSearchState - Algolia searchState object
   */
  const handleSearchStateChange = (updatedSearchState) => {
    // Do nothing if the state has not changed
    if (isSearchStateEqual(searchState, updatedSearchState)) {
      return
    }

    clearTimeout(debouncedSetState)

    const state = (window && window.history && window.history.state) || updatedSearchState
    const url = searchStateToUrl(updatedSearchState) || location.pathname

    setDebouncedSetState(
      setTimeout(() => {
        history.pushState(state, null, url)
      }, 400),
    )

    setSearchState(updatedSearchState)
  }

  /**
   * Updates the searchState using the URL
   */
  const updateSearchStateFromUrl = () => {
    setSearchState(urlToSearchState(location))
  }

  /**
   * This is used to update the searchState whenever the url changes.
   * This should mainly occur whenever the user presses the back button on a search.
   */
  useEffect(() => {
    window.addEventListener('popstate', updateSearchStateFromUrl)

    return () => {
      window.removeEventListener('popstate', updateSearchStateFromUrl)
    }
  }, [])

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName="products"
      searchState={searchState}
      onSearchStateChange={handleSearchStateChange}
    >
      <Configure
        clickAnalytics
        attributesToRetrieve={[
          'objectID',
          'name',
          'avg_rating',
          'reviews_count',
          'slug',
          'subscribable',
          'on_sale',
          'properties',
          'facetable_taxons',
          'producers',
          'image',
          'variants',
          '-variants.cost_price',
        ]}
        facetFilters={facetFilters || []}
        query={searchState.query}
      />
      {children}
    </InstantSearch>
  )
}

AlgoliaContainer.propTypes = {
  isRoot: PropTypes.bool,
  facets: PropTypes.shape({
    taxon: PropTypes.string,
    taxonomy: PropTypes.string,
  }),
  children: PropTypes.func,
}
